import { IconBulb, IconExternalLink } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useIntl } from 'react-intl';

import type { TipsNewsFeedItem, TipsNewsTag } from '@/api/hooks/tipsNewsQueryActions';
import {
  resolveLocalized,
  useDismissTipsNews,
  useMarkTipsNewsSeen,
  useTipsNewsFeed,
} from '@/api/hooks/tipsNewsQueryActions';
import Dropdown from '@/components/atoms/Dropdown';
import { useLocalization } from '@/context/localization';
import { dayjsLocale } from '@/dayjs';
import { useMessages } from '@/hooks/messages';

import { tipsNewsMessages } from './messages';

const TAG_EMOJI: Record<TipsNewsTag, string> = {
  New: '🆕',
  Tip: '💡',
  Info: '📢',
  BestPractice: '⭐',
};

/**
 * EWM-799 / EWM-800: the single Tips & News bell + slide-in panel for the Estimator. The feed merges
 * global and tenant-specific items server-side; this component is intentionally source-agnostic.
 * Opening the panel marks every visible item as seen (clears the unread dot); "Got it" dismisses a
 * single card for the current user.
 */
const TipsNewsDropdown = () => {
  const intl = useIntl();
  const messages = useMessages(tipsNewsMessages);
  const {
    state: { locale },
  } = useLocalization();

  const { data: items = [] } = useTipsNewsFeed();
  const { mutate: markSeen } = useMarkTipsNewsSeen();
  const { mutate: dismiss } = useDismissTipsNews();

  const hasUnread = items.some((item) => !item.isSeen);

  const tagLabel = (tag: TipsNewsTag): string => {
    switch (tag) {
      case 'Tip':
        return messages.tagTip();
      case 'Info':
        return messages.tagInfo();
      case 'BestPractice':
        return messages.tagBestPractice();
      default:
        return messages.tagNew();
    }
  };

  // Opening the panel marks visible items seen. markSeen is idempotent server-side and we only fire
  // it while something is unread, so the close-click (after the dot has cleared) is a no-op.
  const handleTriggerClick = () => {
    if (hasUnread) {
      markSeen();
    }
  };

  const renderCard = (item: TipsNewsFeedItem) => {
    const title = resolveLocalized(item.titleEn, item.titleDe, locale);
    const body = resolveLocalized(item.bodyEn, item.bodyDe, locale);
    const ctaLabel =
      resolveLocalized(item.ctaLabelEn, item.ctaLabelDe, locale) || messages.learnMore();

    return (
      <li
        key={item.id}
        role='none'
        onClick={(e) => e.stopPropagation()}
        className='border-b border-gray-100 px-4 py-3 last:border-b-0 dark:border-white/10'
      >
        <div className='mb-1 flex items-center justify-between gap-2'>
          <span className='text-primary inline-flex items-center gap-1 text-xs font-semibold tracking-wide uppercase dark:text-white'>
            <span aria-hidden>{TAG_EMOJI[item.tag]}</span>
            {tagLabel(item.tag)}
          </span>
          <span className='text-xs font-normal text-gray-400'>
            {dayjs(item.createdAt).locale(dayjsLocale(locale)).fromNow()}
          </span>
        </div>
        {title && <h6 className='text-left text-sm font-semibold'>{title}</h6>}
        {body && (
          <p className='mt-0.5 text-left text-sm whitespace-pre-line text-gray-600 dark:text-gray-300'>
            {body}
          </p>
        )}
        <div className='mt-2 flex items-center justify-between gap-2'>
          {item.ctaUrl ? (
            <a
              href={item.ctaUrl}
              target='_blank'
              rel='noopener noreferrer'
              className='text-primary inline-flex items-center gap-1 text-xs font-medium hover:underline dark:text-white'
            >
              {ctaLabel}
              <IconExternalLink aria-hidden size={14} />
            </a>
          ) : (
            <span />
          )}
          <button
            type='button'
            className='hover:text-primary text-xs font-medium text-gray-500 dark:hover:text-white'
            onClick={() => dismiss(item.id)}
          >
            {messages.dismiss()}
          </button>
        </div>
      </li>
    );
  };

  return (
    <div className='dropdown shrink-0'>
      <Dropdown
        offset={[0, 8]}
        placement='bottom-end'
        poperClass='z-[10000]'
        btnClassName='relative block p-2 rounded-full bg-white-light/40 dark:bg-dark/40 hover:text-primary hover:bg-white-light/90 dark:hover:bg-dark/60'
        dataTestId='tips-news-button'
        button={
          <span onClick={handleTriggerClick} title={messages.ariaLabel()}>
            <IconBulb aria-hidden />
            {hasUnread && (
              <span className='absolute top-0 flex h-3 w-3 ltr:right-0 rtl:left-0'>
                <span className='bg-success/50 absolute -top-[3px] inline-flex h-full w-full animate-ping rounded-full opacity-75 ltr:-left-[3px] rtl:-right-[3px]' />
                <span className='bg-success relative inline-flex h-[6px] w-[6px] rounded-full' />
              </span>
            )}
          </span>
        }
      >
        <div className='dark:bg-c16 overflow-hidden rounded bg-white'>
          <div className='flex items-center justify-between border-b border-gray-100 px-4 py-2 dark:border-white/10'>
            <h4 className='text-lg font-semibold'>{messages.panelTitle()}</h4>
          </div>
          <ul className='text-dark dark:text-white-dark !my-0 max-h-[70vh] w-[320px] overflow-y-auto !py-0 sm:w-[360px]'>
            {items.length === 0 ? (
              <li role='none' className='px-4 py-6 text-center text-sm text-gray-500'>
                {intl.formatMessage(tipsNewsMessages.emptyState)}
              </li>
            ) : (
              items.map(renderCard)
            )}
          </ul>
        </div>
      </Dropdown>
    </div>
  );
};

export default TipsNewsDropdown;
